import { useState, useEffect, useRef, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Calendar,
  Crown,
  Eye,
  X,
  MessageCircle,
  Users,
  Baby,
  ArrowLeft,
  Download,
  EyeOff,
  FileText,
  Trash2,
  Pencil,
  Save,
  UserPlus,
  Sparkles
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import UserConsentModal from "../components/UserConsentModal";
import QuickPasswordResetPanel from "../components/QuickPasswordResetPanel";
import DMModal from "../components/community/DMModal";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 관리자 편집: selectedUser(camelCase) 키 → 백엔드(snake_case) 매핑
const EDIT_FIELD_MAP = {
  phone: "phone", kakaoId: "kakao_id", birth_year: "birth_year",
  country: "country", city: "city", job: "job", maritalStatus: "marital_status",
  singleYears: "single_years",
  height: "height", weight: "weight", appearanceStyle: "appearance_style",
  bloodType: "blood_type", mbti: "mbti", appearanceScore: "appearance_score",
  charmPoint: "charm_point", education: "education", schoolName: "school_name",
  companyName: "company_name", position: "position", income: "income",
  strengthsWeaknesses: "strengths_weaknesses", religion: "religion",
  drinking: "drinking", drinkingAmount: "drinking_amount", smoking: "smoking",
  hobbies: "hobbies", leisureActivities: "leisure_activities",
  favoriteFood: "favorite_food", stressRelief: "stress_relief",
  lifeMotto: "life_motto", favoriteMovie: "favorite_movie",
  favoriteBook: "favorite_book", happyMoments: "happy_moments",
  currentInterests: "current_interests", proudAchievement: "proud_achievement",
  importantValues: "important_values", futureVision: "future_vision",
  idealType: "ideal_type", marriageGoal: "marriage_goal",
  introduction: "introduction",
};

// 관리자 편집 컨텍스트 — InfoItem/ProfileField 가 편집 모드/값/핸들러를 공유
const AdminEditContext = createContext({ editing: false, editData: {}, onEdit: () => {} });

const membershipLabels = {
  basic: { label: "일반회원", color: "bg-stone-100 text-stone-600" },
  regular: { label: "정회원", color: "bg-blue-100 text-blue-600" },
  // standard도 정회원으로 표기 (과거 변환 잔여)
  standard: { label: "정회원", color: "bg-blue-100 text-blue-600" },
  // premium은 '프리미엄'으로 별도 표기
  premium: { label: "프리미엄", color: "bg-rose-100 text-rose-600" },
  vip: { label: "VIP", color: "bg-amber-100 text-amber-600" },
};

// 매칭 컨설팅 상품 배지 (커뮤니티 등급과 별도 표시)
const matchingPlanLabels = {
  elite: "엘리트 매칭",
  highend: "하이앤드 매칭",
  noble: "노블 매칭",
  royal: "로얄 매칭",
};

// 뱃지/드롭다운 동기화용 정규화 헬퍼:
// - standard → regular (정회원)
// - premium → premium (프리미엄, 별도 등급)
// - admin → vip (관리자는 VIP 표기)
// - 알 수 없는/빈 값 → basic (일반)
const normalizeMembership = (m) => {
  const v = (m || "").toLowerCase().trim();
  if (v === "premium") return "premium";
  if (v === "regular" || v === "standard") return "regular";
  if (v === "vip" || v === "admin") return "vip";
  if (v === "basic") return "basic";
  return "basic";
};

const genderLabels = {
  male: "남성",
  female: "여성",
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user, token, loading } = useAuth();
  const currentUserId = user?.id;
  const [dmTarget, setDmTarget] = useState(null);

  const handleDeleteUser = async (userId, label) => {
    const confirmMsg = `정말 '${label}' 회원을 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없습니다.\n- 회원 계정\n- 이 회원이 작성한 모든 글·댓글\n- 주고받은 메시지\n모두 영구 삭제됩니다.`;
    if (!window.confirm(confirmMsg)) return;
    const typed = window.prompt(`한 번 더 확인합니다.\n삭제하려면 '${label}' 을 정확히 입력하세요:`);
    if (typed !== label) {
      toast.error("입력이 일치하지 않아 취소되었습니다.");
      return;
    }
    try {
      const res = await safeFetch(`${API_URL}/api/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const d = data.deleted || {};
        toast.success(`'${label}' 회원 삭제 완료 (글 ${d.posts || 0}개, 댓글 ${d.comments || 0}개 정리됨)`);
        await fetchUsers();
      } else {
        toast.error(data.detail || "삭제에 실패했습니다.");
      }
    } catch {
      toast.error("네트워크 오류가 발생했습니다.");
    }
  };

  // 회원을 회원정보보기 목록에서 숨기기/노출 토글
  const handleToggleHide = async (userId, label, currentlyHidden) => {
    const nextHidden = !currentlyHidden;
    const actionLabel = nextHidden ? "숨기기" : "노출하기";
    if (!window.confirm(`'${label}' 회원을 회원정보보기 목록에서 ${actionLabel}?`)) return;
    try {
      const res = await safeFetch(`${API_URL}/api/admin/users/${userId}/toggle-hide`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ hidden: nextHidden }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(nextHidden ? "회원을 목록에서 숨겼습니다." : "회원을 다시 노출했습니다.");
        await fetchUsers();
      } else {
        toast.error(data.detail || "처리에 실패했습니다.");
      }
    } catch {
      toast.error("네트워크 오류가 발생했습니다.");
    }
  };
  const [users, setUsers] = useState([]);
  const [consentModalUserId, setConsentModalUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [membershipFilter, setMembershipFilter] = useState("");
  const [sortBy, setSortBy] = useState("activity");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [realNameEdit, setRealNameEdit] = useState("");
  const [savingRealName, setSavingRealName] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", name: "", gender: "male", birth_year: "", city: "", membership_type: "regular", phone: "" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [docVerifications, setDocVerifications] = useState({});
  const [pendingDocs, setPendingDocs] = useState([]);
  const [docPreviewUrl, setDocPreviewUrl] = useState(null);
  const [docPreviewType, setDocPreviewType] = useState("image");
  const [docPreviewLoading, setDocPreviewLoading] = useState(null);
  const limit = 20;

  // 권한 체크
  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      navigate("/");
    }
  }, [user, loading, navigate]);

  // 회원 목록 조회
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit,
      });
      if (search) params.append("search", search);
      if (membershipFilter) params.append("membership_type", membershipFilter);
      if (sortBy) params.append("sort", sortBy);

      const response = await safeFetch(`${API_URL}/api/admin/users?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotal(data.total || 0);
      } else {
        const errText = await response.text();
        console.error("[AdminUsers] API error", response.status, errText);
        if (response.status === 401 || response.status === 403) {
          toast.error("관리자 세션이 만료되었습니다. 다시 로그인해 주세요.");
        } else {
          toast.error(`회원 목록을 불러오지 못했습니다 (${response.status})`);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("네트워크 오류로 회원 목록을 불러오지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token && user?.role === "admin") {
      fetchUsers();
      fetchDocVerifications();
    }
  }, [token, user?.role, page, membershipFilter, sortBy]);

  const fetchDocVerifications = async () => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/document-verifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const list = data.verifications || data || [];
        // 최신 제출 건 우선(목록은 submitted_at 내림차순). 뮤테이션 없이 구성:
        // reduceRight 로 오래된→최신 순 처리 → 최신 건이 최종적으로 남음.
        const map = list.reduceRight((acc, d) => ({ ...acc, [d.user_id]: d }), {});
        setDocVerifications(map);
        setPendingDocs(list.filter(d => d.status === "pending"));
      }
    } catch { /* silent */ }
  };

  // 회원관리에서 바로 서류 미리보기 (인증 헤더 필요 → fetch로 받아 data URL 생성)
  const openDocPreview = async (docId, fileUrl) => {
    if (!fileUrl) return;
    setDocPreviewLoading(docId);
    setDocPreviewUrl(null);
    try {
      const res = await safeFetch(`${API_URL}${fileUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error(res.status === 404 ? "파일이 삭제되었거나 존재하지 않습니다." : `서류를 불러오지 못했습니다 (${res.status}).`);
        return;
      }
      const blob = await res.blob();
      const isPdf = blob.type === "application/pdf" || fileUrl.toLowerCase().endsWith(".pdf");
      const objectUrl = URL.createObjectURL(blob);
      setDocPreviewType(isPdf ? "pdf" : "image");
      setDocPreviewUrl(objectUrl);
    } catch {
      toast.error("서류를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setDocPreviewLoading(null);
    }
  };

  const closeDocPreview = () => setDocPreviewUrl(null);

  const handleApproveDoc = async (docId) => {
    if (!window.confirm("서류를 승인하시겠습니까? (10,000P 지급, 원본 파일 즉시 삭제)")) return;
    try {
      const res = await safeFetch(`${API_URL}/api/admin/document-verifications/${docId}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(data.message || "서류가 승인되었습니다.");
        await fetchDocVerifications();
        await fetchUsers();
      } else {
        toast.error(data.detail || "승인에 실패했습니다.");
      }
    } catch (e) {
      toast.error("네트워크 오류가 발생했습니다.");
    }
  };

  const handleRejectDoc = async (docId) => {
    if (!window.confirm("서류를 반려하시겠습니까?")) return;
    try {
      const res = await safeFetch(`${API_URL}/api/admin/document-verifications/${docId}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("서류를 반려했습니다.");
        await fetchDocVerifications();
      } else {
        toast.error(data.detail || "반려에 실패했습니다.");
      }
    } catch {
      toast.error("네트워크 오류가 발생했습니다.");
    }
  };

  // 삭제된 회원의 고아 서류 레코드 완전 정리
  const handleDeleteDocRecord = async (docId) => {
    if (!window.confirm("이 서류 레코드를 완전히 정리(삭제)하시겠습니까?\n(연결된 회원이 없는 고아 데이터입니다)")) return;
    try {
      const res = await safeFetch(`${API_URL}/api/admin/document-verifications/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(data.message || "서류 레코드를 정리했습니다.");
        await fetchDocVerifications();
      } else {
        toast.error(data.detail || "정리에 실패했습니다.");
      }
    } catch {
      toast.error("네트워크 오류가 발생했습니다.");
    }
  };

  // Manually toggle document_verified flag on a user (no submission record required)
  const handleManualDocVerify = async (userId, username, currentlyVerified) => {
    const turningOn = !currentlyVerified;
    let awardPoints = false;
    if (turningOn) {
      if (!window.confirm(`${username}님을 서류인증 완료로 처리하시겠습니까?`)) return;
      awardPoints = window.confirm("10,000P 보너스도 함께 지급하시겠습니까?\n(확인=지급 / 취소=지급 안함)");
    } else {
      if (!window.confirm(`${username}님의 서류인증을 해제하시겠습니까?`)) return;
    }
    try {
      const res = await safeFetch(`${API_URL}/api/admin/users/${userId}/document-verified`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ verified: turningOn, award_points: awardPoints }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(turningOn ? `${username}님 서류인증을 완료 처리했습니다${awardPoints ? " (+10,000P)" : ""}.` : `${username}님 서류인증을 해제했습니다.`);
        await fetchUsers();
        await fetchDocVerifications();
      } else {
        toast.error(data.detail || "처리에 실패했습니다.");
      }
    } catch {
      toast.error("네트워크 오류가 발생했습니다.");
    }
  };

  // 검색
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  // 회원 상세 보기
  const handleViewDetail = async (userId) => {
    try {
      const response = await safeFetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // snake_case → camelCase 정규화 (백엔드/DB 양쪽 형식 지원)
        const normalized = {
          ...data,
          kakaoId: data.kakao_id || data.kakaoId || "",
          maritalStatus: data.marital_status || data.maritalStatus || "",
          singleYears: data.single_years || data.singleYears || "",
          hasChildren: data.has_children || data.hasChildren || "",
          childrenList: data.children_list || data.childrenList || [],
          nameEnglish: data.name_english || data.nameEnglish || "",
          schoolName: data.school_name || data.schoolName || "",
          companyName: data.company_name || data.companyName || "",
          incomePrivate: data.income_private ?? data.incomePrivate ?? false,
          appearanceStyle: data.appearance_style || data.appearanceStyle || "",
          bloodType: data.blood_type || data.bloodType || "",
          appearanceScore: data.appearance_score || data.appearanceScore || "",
          charmPoint: data.charm_point || data.charmPoint || "",
          drinkingAmount: data.drinking_amount || data.drinkingAmount || "",
          strengthsWeaknesses: data.strengths_weaknesses || data.strengthsWeaknesses || "",
          leisureActivities: data.leisure_activities || data.leisureActivities || "",
          favoriteFood: data.favorite_food || data.favoriteFood || "",
          stressRelief: data.stress_relief || data.stressRelief || "",
          lifeMotto: data.life_motto || data.lifeMotto || "",
          favoriteMovie: data.favorite_movie || data.favoriteMovie || "",
          favoriteBook: data.favorite_book || data.favoriteBook || "",
          happyMoments: data.happy_moments || data.happyMoments || "",
          currentInterests: data.current_interests || data.currentInterests || "",
          proudAchievement: data.proud_achievement || data.proudAchievement || "",
          importantValues: data.important_values || data.importantValues || "",
          futureVision: data.future_vision || data.futureVision || "",
          idealType: data.ideal_type || data.idealType || "",
          marriageGoal: data.marriage_goal || data.marriageGoal || "",
        };
        setSelectedUser(normalized);
        setRealNameEdit(data.real_name || "");
        setEditMode(false);
        setEditData({});
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error("Error fetching user detail:", error);
    }
  };

  // 멤버십 변경
  const handleSaveRealName = async () => {
    if (!selectedUser) return;
    setSavingRealName(true);
    try {
      const res = await safeFetch(`${API_URL}/api/admin/users/${selectedUser.id}/real-name`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ real_name: realNameEdit.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "저장에 실패했습니다.");
      toast.success("실명이 저장되었습니다.");
      setSelectedUser({ ...selectedUser, real_name: realNameEdit.trim() });
      fetchUsers();
    } catch (e) {
      toast.error(e.message || "저장에 실패했습니다.");
    } finally {
      setSavingRealName(false);
    }
  };
  const handleChangeGender = async (userId, newGender) => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/users/${userId}/gender`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ gender: newGender }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "성별 저장에 실패했습니다.");
      toast.success(`성별이 ${newGender === "male" ? "남성" : "여성"}(으)로 저장되었습니다.`);
      if (selectedUser?.id === userId) setSelectedUser({ ...selectedUser, gender: newGender });
      fetchUsers();
    } catch (e) {
      toast.error(e.message || "성별 저장에 실패했습니다.");
    }
  };
  // 관리자 상세정보 편집
  const startEdit = () => {
    setEditData({ ...selectedUser });
    setEditMode(true);
  };
  const handleCreateUser = async () => {
    const email = (createForm.email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setCreateResult({ type: "error", message: "유효한 이메일을 입력해주세요." });
      return;
    }
    setCreatingUser(true);
    setCreateResult(null);
    try {
      const payload = {
        email,
        name: createForm.name,
        gender: createForm.gender,
        birth_year: createForm.birth_year ? parseInt(createForm.birth_year, 10) : null,
        city: createForm.city,
        phone: createForm.phone,
        membership_type: createForm.membership_type,
      };
      const res = await safeFetch(`${API_URL}/api/admin/users/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "회원 생성에 실패했습니다.");
      setCreateResult({ type: "success", message: `${data.username} 회원 생성 완료!`, tempPassword: data.temp_password });
      toast.success("회원이 생성되었습니다.");
      fetchUsers();
    } catch (e) {
      setCreateResult({ type: "error", message: e.message || "회원 생성에 실패했습니다." });
    } finally {
      setCreatingUser(false);
    }
  };
  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };
  const handleEditField = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };
  const handleSaveProfile = async () => {
    if (!selectedUser) return;
    setSavingProfile(true);
    try {
      const payload = {};
      Object.entries(EDIT_FIELD_MAP).forEach(([camel, snake]) => {
        const v = editData[camel];
        if (v !== undefined) payload[snake] = typeof v === "string" ? v.trim() : v;
      });
      const res = await safeFetch(`${API_URL}/api/admin/users/${selectedUser.id}/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "저장에 실패했습니다.");
      toast.success("회원 정보가 저장되었습니다.");
      // 화면에 즉시 반영 (편집한 camelCase 값 병합)
      const merged = { ...selectedUser };
      Object.keys(EDIT_FIELD_MAP).forEach((camel) => {
        if (editData[camel] !== undefined) merged[camel] = editData[camel];
      });
      if (data.user?.location !== undefined) merged.location = data.user.location;
      setSelectedUser(merged);
      setEditMode(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.message || "저장에 실패했습니다.");
    } finally {
      setSavingProfile(false);
    }
  };


  const handleChangeMembership = async (userId, newMembership) => {
    if (!window.confirm(`멤버십을 ${membershipLabels[newMembership]?.label || newMembership}(으)로 변경하시겠습니까?`)) {
      return;
    }

    try {
      const response = await safeFetch(
        `${API_URL}/api/admin/users/${userId}/membership?membership_type=${newMembership}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        alert("멤버십이 변경되었습니다.");
        fetchUsers();
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser({ ...selectedUser, membership_type: newMembership });
        }
      }
    } catch (error) {
      console.error("Error updating membership:", error);
      alert("멤버십 변경에 실패했습니다.");
    }
  };

  // PDF 저장 기능 (html2canvas 방식 - 한글 완벽 지원)
  const handleDownloadPDF = async (user) => {
    const modalEl = document.getElementById("user-detail-modal-content");
    if (!modalEl) return;

    // PDF 버튼과 닫기 버튼 임시 숨김
    const hideEls = modalEl.querySelectorAll("[data-pdf-hide]");
    hideEls.forEach(el => el.style.display = "none");

    // 스크롤 컨테이너 제약을 임시 해제하여 전체 내용(여러 페이지)을 캡처
    const prevStyle = {
      maxHeight: modalEl.style.maxHeight,
      height: modalEl.style.height,
      overflow: modalEl.style.overflow,
    };
    modalEl.style.maxHeight = "none";
    modalEl.style.height = "auto";
    modalEl.style.overflow = "visible";

    try {
      // 레이아웃 반영 대기
      await new Promise((r) => setTimeout(r, 80));
      const canvas = await html2canvas(modalEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
        windowWidth: modalEl.scrollWidth,
        windowHeight: modalEl.scrollHeight,
        height: modalEl.scrollHeight,
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pdfWidth - margin * 2;
      // 한 페이지(여백 제외)에 해당하는 원본 캔버스의 픽셀 높이
      const pageHeightPx = Math.floor(((pdfHeight - margin * 2) * canvas.width) / imgWidth);

      // 페이지 경계에서 글자/항목이 잘리지 않도록 여백(흰 줄)을 찾아 분할 지점 결정
      const srcCtx = canvas.getContext("2d");
      const findCleanBreak = (startY, idealHeight) => {
        const endY = Math.min(startY + idealHeight, canvas.height);
        if (endY >= canvas.height) return endY - startY; // 마지막 페이지
        // 이상적 위치에서 위로 최대 15% 범위를 스캔하여 빈 가로줄 탐색
        const minHeight = Math.floor(idealHeight * 0.85);
        for (let y = endY; y > startY + minHeight; y--) {
          let isBlank = true;
          try {
            const row = srcCtx.getImageData(0, y, canvas.width, 1).data;
            for (let i = 0; i < row.length; i += 4 * 8) {
              // 흰색(밝기 높음)이 아니면 콘텐츠가 있는 줄
              if (row[i] < 245 || row[i + 1] < 245 || row[i + 2] < 245) {
                isBlank = false;
                break;
              }
            }
          } catch (e) {
            return idealHeight;
          }
          if (isBlank) return y - startY;
        }
        return idealHeight;
      };

      let renderedPx = 0;
      let page = 0;
      while (renderedPx < canvas.height) {
        const sliceHeightPx = findCleanBreak(renderedPx, pageHeightPx);

        // 페이지별 슬라이스를 임시 캔버스에 그려 상단부터 선명하게 출력
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeightPx;
        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0, renderedPx, canvas.width, sliceHeightPx,
          0, 0, canvas.width, sliceHeightPx
        );

        const sliceImgData = pageCanvas.toDataURL("image/png");
        const sliceImgHeight = (sliceHeightPx * imgWidth) / canvas.width;

        if (page > 0) pdf.addPage();
        pdf.addImage(sliceImgData, "PNG", margin, margin, imgWidth, sliceImgHeight);

        renderedPx += sliceHeightPx;
        page++;
      }

      const fileName = `New Coupling_자기소개서_${user.name || user.username || "user"}_${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);
    } finally {
      modalEl.style.maxHeight = prevStyle.maxHeight;
      modalEl.style.height = prevStyle.height;
      modalEl.style.overflow = prevStyle.overflow;
      hideEls.forEach(el => el.style.display = "");
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-burgundy-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-stone-800">회원 관리</h1>
            <p className="text-stone-500 mt-1">전체 {total}명의 회원</p>
          </div>
          <button
            onClick={() => { setCreateForm({ email: "", name: "", gender: "male", birth_year: "", city: "", membership_type: "regular", phone: "" }); setCreateResult(null); setShowCreateModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-burgundy-500 hover:bg-burgundy-600 text-white rounded-lg text-sm font-semibold transition-colors shrink-0"
            data-testid="create-member-btn"
          >
            <UserPlus className="w-4 h-4" />
            회원 직접 생성
          </button>
        </div>

        {/* Quick admin tool: reset any member's password without email */}
        <QuickPasswordResetPanel />

        {/* 서류인증 대기 목록 (회원관리 안에서 바로 확인·승인) */}
        {pendingDocs.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-amber-200 mb-6 overflow-hidden" data-testid="pending-docs-panel">
            <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-b border-amber-100">
              <FileText className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-bold text-amber-800">서류인증 대기 {pendingDocs.length}건</h3>
              <span className="text-xs text-amber-600">제출된 혼인관계증명서를 바로 확인하고 승인/반려하세요.</span>
            </div>
            <ul className="divide-y divide-stone-100">
              {pendingDocs.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`pending-doc-${doc.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800 truncate flex items-center gap-1.5">
                      {doc.linked_username || doc.username || "이름없음"}
                      {doc.user_exists === false && (
                        <span className="text-[10px] font-semibold bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded" data-testid={`pending-doc-orphan-${doc.id}`}>삭제된 회원</span>
                      )}
                    </div>
                    <div className="text-xs text-stone-400 truncate">
                      {doc.linked_email || doc.email} · {(doc.submitted_at || "").slice(0, 10)}
                      {doc.user_exists && doc.linked_email && doc.email && doc.linked_email !== doc.email && (
                        <span className="text-amber-500"> · 제출시 이메일 {doc.email}</span>
                      )}
                    </div>
                  </div>
                  {doc.user_exists === false ? (
                    <button onClick={() => handleDeleteDocRecord(doc.id)} className="text-xs font-medium bg-stone-200 text-stone-700 px-3 py-1.5 rounded-md hover:bg-stone-300" data-testid={`pending-doc-cleanup-${doc.id}`}>레코드 정리</button>
                  ) : (
                    <>
                      <button onClick={() => handleViewDetail(doc.user_id)} className="text-xs font-medium bg-stone-100 text-stone-700 px-3 py-1.5 rounded-md hover:bg-stone-200" data-testid={`pending-doc-member-${doc.id}`}>회원 보기</button>
                      <button
                        onClick={() => openDocPreview(doc.id, `/api/admin/document-view/${doc.id}`)}
                        disabled={docPreviewLoading === doc.id}
                        className="text-xs font-medium bg-burgundy-500 text-white px-3 py-1.5 rounded-md hover:bg-burgundy-600 disabled:opacity-50"
                        data-testid={`pending-doc-view-${doc.id}`}
                      >
                        {docPreviewLoading === doc.id ? "여는중…" : "서류보기"}
                      </button>
                      <button onClick={() => handleApproveDoc(doc.id)} className="text-xs font-medium bg-emerald-500 text-white px-3 py-1.5 rounded-md hover:bg-emerald-600" data-testid={`pending-doc-approve-${doc.id}`}>승인</button>
                      <button onClick={() => handleRejectDoc(doc.id)} className="text-xs font-medium bg-red-100 text-red-600 px-3 py-1.5 rounded-md hover:bg-red-200" data-testid={`pending-doc-reject-${doc.id}`}>반려</button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="이름, 닉네임, 이메일, 전화번호로 검색"
                  className="w-full pl-10 pr-4 py-2.5 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                />
              </div>
            </form>

            {/* Membership Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-stone-400" />
              <select
                value={membershipFilter}
                onChange={(e) => {
                  setMembershipFilter(e.target.value);
                  setPage(0);
                }}
                className="px-4 py-2.5 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
              >
                <option value="">전체 멤버십</option>
                <option value="basic">일반회원</option>
                <option value="regular">정회원</option>
                <option value="vip">VIP</option>
              </select>
            </div>

            {/* Sort Toggle */}
            <div className="flex items-center gap-2">
              <select
                data-testid="admin-users-sort-select"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(0);
                }}
                className="px-4 py-2.5 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
              >
                <option value="activity">최근 활동순</option>
                <option value="created">최근 가입순</option>
                <option value="upgraded">최근 전환순(결제·정회원)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-stone-500">로딩 중...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-stone-500">회원이 없습니다.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">회원정보</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">연락처</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">멤버십</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">가입일 / 전환일</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-stone-600 uppercase">서류인증</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-stone-600 uppercase">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-stone-50 transition-colors">
                      {/* 회원정보 */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden relative">
                            {u.profile_image ? (
                              <>
                                <img
                                  src={u.profile_image.startsWith("http") ? u.profile_image : `${API_URL}${u.profile_image}`}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const fb = e.currentTarget.nextElementSibling;
                                    if (fb) fb.style.display = "flex";
                                  }}
                                />
                                <div className="absolute inset-0 hidden items-center justify-center bg-stone-200">
                                  <User className="w-5 h-5 text-stone-400" />
                                </div>
                              </>
                            ) : (
                              <User className="w-5 h-5 text-stone-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-stone-800 flex items-center gap-1.5 flex-wrap">
                              <span>{u.name || "이름 미입력"}</span>
                              {u.username && (
                                <span className="text-stone-400 text-xs font-normal">
                                  @{u.username}
                                </span>
                              )}
                              {u.nickname && u.nickname !== u.username && (
                                <span className="text-stone-400 text-xs font-normal">
                                  ({u.nickname})
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-stone-500">
                              {genderLabels[u.gender] || "-"} · {u.birth_year ? `${u.birth_year}년생` : "-"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* 연락처 */}
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <p className="text-sm text-stone-600 flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />
                            {u.email}
                          </p>
                          {u.phone && (
                            <p className="text-sm text-amber-600 font-medium flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />
                              {u.phone}
                            </p>
                          )}
                          {u.kakao_id && (
                            <p className="text-sm text-amber-600 flex items-center gap-1">
                              <MessageCircle className="w-3.5 h-3.5" />
                              {u.kakao_id}
                            </p>
                          )}
                        </div>
                      </td>

                      {/* 멤버십 — 뱃지/드롭다운 동기화 */}
                      <td className="px-4 py-4">
                        {(() => {
                          const norm = normalizeMembership(u.membership_type);
                          const isAdmin = (u.role === "admin" || u.membership_type === "admin");
                          const label = isAdmin ? "관리자" : (membershipLabels[norm]?.label || "일반회원");
                          const color = isAdmin ? "bg-purple-100 text-purple-600" : (membershipLabels[norm]?.color || "bg-stone-100 text-stone-600");
                          return (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
                              <Crown className="w-3 h-3" />
                              {label}
                            </span>
                          );
                        })()}
                        {matchingPlanLabels[u.matching_plan] && (
                          <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-burgundy-50 text-burgundy-600 border border-burgundy-100" data-testid={`user-matching-badge-${u.id}`}>
                            <Sparkles className="w-2.5 h-2.5" /> {matchingPlanLabels[u.matching_plan]}
                          </span>
                        )}
                        <p className="text-xs text-stone-400 mt-1">{u.points || 0} P</p>
                      </td>

                      {/* 가입일 / 전환일 */}
                      <td className="px-4 py-4">
                        <p className="text-sm text-stone-600">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("ko-KR") : "-"}
                        </p>
                        {u.upgrade_date && !["basic", "admin"].includes(normalizeMembership(u.membership_type)) && (
                          <p className="text-[11px] text-blue-500 mt-0.5" data-testid={`user-upgrade-date-${u.id}`}>
                            전환 {new Date(u.upgrade_date).toLocaleDateString("ko-KR")}
                          </p>
                        )}
                      </td>

                      {/* 서류인증 */}
                      <td className="px-4 py-4 text-center">
                        {(() => {
                          const doc = docVerifications[u.id];
                          if (u.document_verified) {
                            return (
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">승인완료</span>
                                <button
                                  onClick={() => handleManualDocVerify(u.id, u.username, true)}
                                  className="text-[10px] font-medium bg-stone-100 text-stone-500 px-2 py-1 rounded-md hover:bg-red-100 hover:text-red-600"
                                  data-testid={`doc-unverify-${u.id}`}
                                  title="인증 해제"
                                >해제</button>
                              </div>
                            );
                          }
                          if (doc && doc.status === "pending") {
                            return (
                              <div className="flex flex-col items-center justify-center gap-1">
                                <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">심사대기</span>
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={() => openDocPreview(doc.id, `/api/admin/document-view/${doc.id}`)} disabled={docPreviewLoading === doc.id} className="text-[10px] font-medium bg-burgundy-500 text-white px-2 py-1 rounded-md hover:bg-burgundy-600 disabled:opacity-50" data-testid={`doc-preview-${u.id}`}>{docPreviewLoading === doc.id ? "여는중" : "서류보기"}</button>
                                  <button onClick={() => handleApproveDoc(doc.id)} className="text-[10px] font-medium bg-emerald-500 text-white px-2 py-1 rounded-md hover:bg-emerald-600" data-testid={`doc-approve-${u.id}`}>승인</button>
                                  <button onClick={() => handleRejectDoc(doc.id)} className="text-[10px] font-medium bg-red-100 text-red-600 px-2 py-1 rounded-md hover:bg-red-200" data-testid={`doc-reject-${u.id}`}>반려</button>
                                </div>
                              </div>
                            );
                          }
                          if (doc && doc.status === "rejected") {
                            return (
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xs font-medium bg-red-100 text-red-600 px-2 py-1 rounded-full">반려</span>
                                {doc.file_url && (
                                  <button onClick={() => openDocPreview(doc.id, `/api/admin/document-view/${doc.id}`)} disabled={docPreviewLoading === doc.id} className="text-[10px] font-medium bg-burgundy-500 text-white px-2 py-1 rounded-md hover:bg-burgundy-600 disabled:opacity-50" data-testid={`doc-preview-${u.id}`}>서류보기</button>
                                )}
                                <button
                                  onClick={() => handleManualDocVerify(u.id, u.username, false)}
                                  className="text-[10px] font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md hover:bg-emerald-200"
                                  data-testid={`doc-manual-${u.id}`}
                                >수동 인증</button>
                              </div>
                            );
                          }
                          // 미제출
                          return (
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-xs text-stone-400">미제출</span>
                              <button
                                onClick={() => handleManualDocVerify(u.id, u.username, false)}
                                className="text-[10px] font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md hover:bg-emerald-200"
                                data-testid={`doc-manual-${u.id}`}
                                title="관리자가 수동으로 서류인증 완료 처리"
                              >수동 인증</button>
                            </div>
                          );
                        })()}
                      </td>

                      {/* 관리 */}
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleViewDetail(u.id)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-burgundy-500 hover:bg-burgundy-600 rounded-lg transition-colors flex items-center gap-1"
                            data-testid={`view-detail-${u.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            상세보기
                          </button>
                          <button
                            onClick={() => setDmTarget({ id: u.id, name: u.username || u.real_name || u.name || "회원" })}
                            className="px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors flex items-center gap-1 border border-purple-200"
                            data-testid={`send-message-${u.id}`}
                            title="메시지 보내기"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            메시지
                          </button>
                          <select
                            value={normalizeMembership(u.membership_type)}
                            onChange={(e) => handleChangeMembership(u.id, e.target.value)}
                            className="text-xs px-2 py-1 border border-stone-200 rounded-lg focus:outline-none"
                            data-testid={`membership-select-${u.id}`}
                          >
                            <option value="basic">일반</option>
                            <option value="regular">정회원</option>
                            <option value="premium">프리미엄</option>
                            <option value="vip">VIP</option>
                          </select>
                          <button
                            onClick={() => setConsentModalUserId(u.id)}
                            className="px-2.5 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors flex items-center gap-1 border border-violet-200"
                            data-testid={`view-consents-${u.id}`}
                            title="가입 동의 이력 보기"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            동의
                          </button>
                          {u.id !== currentUserId && (
                            <button
                              onClick={() => handleToggleHide(u.id, u.username || u.email, !!u.hidden_from_listing)}
                              className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 border ${
                                u.hidden_from_listing
                                  ? "text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200"
                                  : "text-stone-600 bg-stone-50 hover:bg-stone-100 border-stone-200"
                              }`}
                              data-testid={`toggle-hide-user-${u.id}`}
                              title={u.hidden_from_listing ? "회원정보보기 목록에서 다시 노출" : "회원정보보기 목록에서 숨김"}
                            >
                              <EyeOff className="w-3.5 h-3.5" />
                              {u.hidden_from_listing ? "노출" : "숨기기"}
                            </button>
                          )}
                          {u.role !== "admin" && u.id !== currentUserId && (
                            <button
                              onClick={() => handleDeleteUser(u.id, u.username || u.email)}
                              className="px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-1 border border-red-100"
                              data-testid={`delete-user-${u.id}`}
                              title="회원 삭제"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              삭제
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination — always show count, navigation only when needed */}
          {total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200">
              <p className="text-sm text-stone-500" data-testid="admin-users-pagination-info">
                {total === 0 ? "회원이 없습니다" : `${page * limit + 1} - ${Math.min((page + 1) * limit, total)} / ${total}명`}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="p-2 rounded-lg border border-stone-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-stone-600">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-2 rounded-lg border border-stone-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {/* 회원 직접 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-member-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-stone-200 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-burgundy-500" /> 회원 직접 생성
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-stone-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-stone-500">이메일 *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="example@daum.net"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-300 focus:border-burgundy-400 focus:ring-1 focus:ring-burgundy-200 outline-none text-sm"
                  data-testid="create-email-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500">이름</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="홍길동"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-300 focus:border-burgundy-400 focus:ring-1 focus:ring-burgundy-200 outline-none text-sm"
                  data-testid="create-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-stone-500">성별</label>
                  <select
                    value={createForm.gender}
                    onChange={(e) => setCreateForm({ ...createForm, gender: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-300 outline-none text-sm bg-white"
                    data-testid="create-gender-select"
                  >
                    <option value="male">남성</option>
                    <option value="female">여성</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500">출생년도</label>
                  <input
                    type="number"
                    value={createForm.birth_year}
                    onChange={(e) => setCreateForm({ ...createForm, birth_year: e.target.value })}
                    placeholder="1965"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-300 focus:border-burgundy-400 outline-none text-sm"
                    data-testid="create-birthyear-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-stone-500">거주지</label>
                  <input
                    type="text"
                    value={createForm.city}
                    onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                    placeholder="서울"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-300 focus:border-burgundy-400 outline-none text-sm"
                    data-testid="create-city-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500">회원등급</label>
                  <select
                    value={createForm.membership_type}
                    onChange={(e) => setCreateForm({ ...createForm, membership_type: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-300 outline-none text-sm bg-white"
                    data-testid="create-membership-select"
                  >
                    <option value="basic">일반회원</option>
                    <option value="regular">정회원</option>
                    <option value="premium">프리미엄</option>
                    <option value="vip">VIP</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500">전화번호 (선택)</label>
                <input
                  type="text"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="010-0000-0000"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-300 focus:border-burgundy-400 outline-none text-sm"
                  data-testid="create-phone-input"
                />
              </div>

              {createResult && (
                <div className={`p-3 rounded-lg text-sm ${createResult.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="create-result">
                  <p className="font-medium">{createResult.message}</p>
                  {createResult.tempPassword && (
                    <p className="mt-1 text-xs">임시 비밀번호: <span className="font-mono font-bold bg-white px-1.5 py-0.5 rounded border">{createResult.tempPassword}</span><br/>회원에게 전달하고 로그인 후 변경하도록 안내하세요.</p>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleCreateUser}
                  disabled={creatingUser}
                  className="flex-1 py-2.5 bg-burgundy-500 hover:bg-burgundy-600 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  data-testid="create-submit-btn"
                >
                  {creatingUser ? "생성 중…" : "회원 생성"}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-lg text-sm font-medium"
                  data-testid="create-close-btn"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" id="user-detail-modal-content">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between" data-pdf-hide>
              <h2 className="text-lg font-bold text-stone-800">회원 상세정보</h2>
              <div className="flex items-center gap-2">
                {editMode ? (
                  <>
                    <button
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      data-testid="member-edit-save-btn"
                    >
                      <Save className="w-4 h-4" />
                      {savingProfile ? "저장 중…" : "변경사항 저장"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={savingProfile}
                      className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-lg text-sm font-medium transition-colors"
                      data-testid="member-edit-cancel-btn"
                    >
                      취소
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={startEdit}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                      data-testid="member-edit-btn"
                    >
                      <Pencil className="w-4 h-4" />
                      편집
                    </button>
                    <button
                      onClick={() => handleDownloadPDF(selectedUser)}
                      className="flex items-center gap-2 px-4 py-2 bg-burgundy-500 hover:bg-burgundy-600 text-white rounded-lg text-sm font-medium transition-colors"
                      data-pdf-hide
                      data-testid="member-pdf-save-btn"
                    >
                      <Download className="w-4 h-4" />
                      PDF 저장
                    </button>
                  </>
                )}
                <button
                  onClick={() => { setShowDetailModal(false); setEditMode(false); }}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                  data-pdf-hide
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <AdminEditContext.Provider value={{ editing: editMode, editData, onEdit: handleEditField }}>
            <div className="p-6 space-y-6">
              {/* Profile Header */}
              <div className="flex items-center gap-4">
                <div
                  role={selectedUser.profile_image ? "button" : undefined}
                  tabIndex={selectedUser.profile_image ? 0 : undefined}
                  onClick={() => selectedUser.profile_image && setPreviewImage(selectedUser.profile_image)}
                  onKeyDown={(e) => {
                    if (selectedUser.profile_image && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setPreviewImage(selectedUser.profile_image);
                    }
                  }}
                  className={`w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden transition-all ${selectedUser.profile_image ? "cursor-zoom-in hover:ring-4 hover:ring-burgundy-200" : ""}`}
                  data-testid="admin-user-profile-thumb"
                  aria-label={selectedUser.profile_image ? "프로필 사진 확대" : undefined}
                >
                  {selectedUser.profile_image ? (
                    <img
                      src={selectedUser.profile_image && !selectedUser.profile_image.startsWith("http") ? `${API_URL}${selectedUser.profile_image}` : selectedUser.profile_image}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // 이미지 로드 실패 시 기본 User 아이콘으로 폴백
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.nextElementSibling;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    style={{ display: selectedUser.profile_image ? "none" : "flex" }}
                    className="w-full h-full items-center justify-center"
                  >
                    <User className="w-10 h-10 text-stone-400" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-stone-800">
                    {selectedUser.real_name || selectedUser.name || selectedUser.username}
                  </h3>
                  <p className="text-stone-500" data-pdf-hide>{selectedUser.nickname && `@${selectedUser.nickname}`}</p>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium mt-2 ${membershipLabels[normalizeMembership(selectedUser.membership_type)]?.color}`}>
                    <Crown className="w-3 h-3" />
                    {membershipLabels[normalizeMembership(selectedUser.membership_type)]?.label}
                  </span>
                  {matchingPlanLabels[selectedUser.matching_plan] && (
                    <span className="ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold mt-2 bg-burgundy-50 text-burgundy-600 border border-burgundy-100" data-testid="detail-matching-badge">
                      <Sparkles className="w-3 h-3" /> {matchingPlanLabels[selectedUser.matching_plan]}
                    </span>
                  )}
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <InfoItem icon={<Mail className="w-4 h-4" />} label="이메일" value={selectedUser.email} pdfHide />
                <InfoItem icon={<User className="w-4 h-4" />} label="아이디 (닉네임)" value={selectedUser.username || "-"} pdfHide />
                <InfoItem icon={<Phone className="w-4 h-4" />} label="전화번호" value={selectedUser.phone || "-"} highlight pdfHide field="phone" />
                <InfoItem icon={<MessageCircle className="w-4 h-4" />} label="카카오톡 ID" value={selectedUser.kakaoId || "-"} highlight pdfHide field="kakaoId" />
                <InfoItem icon={<User className="w-4 h-4" />} label="성별" value={genderLabels[selectedUser.gender] || "-"} />
                <InfoItem icon={<Calendar className="w-4 h-4" />} label="출생년도" value={selectedUser.birth_year ? `${selectedUser.birth_year}년` : "-"} field="birth_year" type="number" />
                <InfoItem icon={<MapPin className="w-4 h-4" />} label="거주지" value={`${selectedUser.country || ""} ${selectedUser.city || ""}`.trim() || selectedUser.location || "-"} dualFields={[{ key: "country", ph: "국가" }, { key: "city", ph: "도시" }]} />
                <InfoItem icon={<Briefcase className="w-4 h-4" />} label="직업" value={selectedUser.job || "-"} field="job" />
                <InfoItem icon={<Users className="w-4 h-4" />} label="싱글여부" value={selectedUser.maritalStatus || "-"} field="maritalStatus" />
                <InfoItem icon={<Calendar className="w-4 h-4" />} label="이혼/사별 경과기간" value={selectedUser.singleYears || "-"} field="singleYears" />
              </div>

              {/* 실명(real_name) 확인/수정 — 정회원 이상 회원보기·프로필에 노출 */}
              <div className="bg-burgundy-50 border border-burgundy-100 rounded-xl p-4" data-testid="admin-real-name-block" data-pdf-hide>
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-burgundy-600" />
                  <span className="font-semibold text-burgundy-800 text-sm">실명 (회원보기/프로필 노출)</span>
                  {selectedUser.real_name_consent && (
                    <span className="text-[11px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">노출 동의됨</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={realNameEdit}
                    onChange={(e) => setRealNameEdit(e.target.value)}
                    placeholder="실명 미입력"
                    className="flex-1 px-3 py-2 rounded-lg border border-stone-200 focus:border-burgundy-400 focus:ring-2 focus:ring-burgundy-100 outline-none text-sm"
                    data-testid="admin-real-name-input"
                  />
                  <button
                    onClick={handleSaveRealName}
                    disabled={savingRealName}
                    className="px-4 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-700 text-white text-sm font-medium transition disabled:opacity-50"
                    data-testid="admin-real-name-save"
                  >
                    {savingRealName ? "저장 중…" : "저장"}
                  </button>
                </div>
              </div>

              {/* 성별 입력/수정 — 미입력 시 성별 탭에 노출되지 않으므로 관리자가 지정 */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4" data-testid="admin-gender-block" data-pdf-hide>
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-blue-800 text-sm">성별 (남성/여성 탭 노출)</span>
                  {!selectedUser.gender && (
                    <span className="text-[11px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">미입력</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {["male", "female"].map((g) => (
                    <button
                      key={g}
                      onClick={() => handleChangeGender(selectedUser.id, g)}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition border ${
                        selectedUser.gender === g
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-stone-600 border-stone-200 hover:bg-blue-50"
                      }`}
                      data-testid={`admin-gender-${g}`}
                    >
                      {g === "male" ? "남성" : "여성"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Children */}
              {selectedUser.hasChildren === "yes" && selectedUser.childrenList?.length > 0 && (
                <div className="bg-stone-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Baby className="w-4 h-4 text-stone-500" />
                    <span className="font-medium text-stone-700">자녀 정보</span>
                  </div>
                  <div className="space-y-2">
                    {selectedUser.childrenList.map((child, idx) => (
                      <p key={idx} className="text-sm text-stone-600">
                        {child.gender} {child.age}세 ({child.livingStatus})
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Personality Tags */}
              {selectedUser.personality?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-stone-700 mb-2">성격 키워드</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.personality.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-burgundy-50 text-burgundy-600 rounded-full text-sm">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 자기소개서 전체 보기 (Google Form 양식 기반) */}
              <div className="border-t border-stone-200 pt-4">
                <p className="text-sm font-semibold text-burgundy-600 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  자기소개서 전체
                </p>
                
                <div className="space-y-4 bg-stone-50 rounded-xl p-4">
                  {/* 외모/신체 정보 */}
                  <ProfileSection title="외모/신체 정보">
                    <ProfileField label="키" value={selectedUser.height} field="height" />
                    <ProfileField label="몸무게" value={selectedUser.weight} field="weight" />
                    <ProfileField label="외모스타일" value={selectedUser.appearanceStyle} field="appearanceStyle" />
                    <ProfileField label="혈액형" value={selectedUser.bloodType} field="bloodType" />
                    <ProfileField label="MBTI" value={selectedUser.mbti} field="mbti" />
                    <ProfileField label="외모점수" value={selectedUser.appearanceScore ? `${selectedUser.appearanceScore}점` : null} field="appearanceScore" type="number" />
                    <ProfileField label="매력포인트" value={selectedUser.charmPoint} field="charmPoint" />
                  </ProfileSection>

                  {/* 학력/직업 */}
                  <ProfileSection title="학력/직업">
                    <ProfileField label="최종학력" value={selectedUser.education} field="education" />
                    <ProfileField label="학교명" value={selectedUser.schoolName} field="schoolName" />
                    <ProfileField label="직업" value={selectedUser.job} field="job" />
                    <ProfileField label="회사명" value={selectedUser.companyName} field="companyName" />
                    <ProfileField label="직위" value={selectedUser.position} field="position" />
                    <ProfileField label="연봉/자산" value={selectedUser.incomePrivate ? "비공개" : selectedUser.income} field="income" />
                  </ProfileSection>

                  {/* 성격/생활 */}
                  <ProfileSection title="성격/생활">
                    <ProfileField label="성격/장단점" value={selectedUser.strengthsWeaknesses} fullWidth field="strengthsWeaknesses" />
                    <ProfileField label="종교" value={selectedUser.religion} field="religion" />
                    <ProfileField label="음주" value={selectedUser.drinking} field="drinking" />
                    <ProfileField label="주량" value={selectedUser.drinkingAmount} field="drinkingAmount" />
                    <ProfileField label="흡연" value={selectedUser.smoking} field="smoking" />
                  </ProfileSection>

                  {/* 취미/관심사 */}
                  <ProfileSection title="취미/관심사">
                    <ProfileField label="좋아하는 취미" value={selectedUser.hobbies} fullWidth field="hobbies" />
                    <ProfileField label="여가활동/운동/자기개발" value={selectedUser.leisureActivities} fullWidth field="leisureActivities" />
                    <ProfileField label="좋아하는 음식" value={selectedUser.favoriteFood} field="favoriteFood" />
                    <ProfileField label="스트레스 해소법" value={selectedUser.stressRelief} field="stressRelief" />
                  </ProfileSection>

                  {/* 가치관/인생관 */}
                  <ProfileSection title="가치관/인생관">
                    <ProfileField label="인생좌우명" value={selectedUser.lifeMotto} field="lifeMotto" />
                    <ProfileField label="인생영화" value={selectedUser.favoriteMovie} field="favoriteMovie" />
                    <ProfileField label="인생 책" value={selectedUser.favoriteBook} field="favoriteBook" />
                    <ProfileField label="행복을 느끼는 순간" value={selectedUser.happyMoments} fullWidth field="happyMoments" />
                    <ProfileField label="현재 관심사" value={selectedUser.currentInterests} fullWidth field="currentInterests" />
                    <ProfileField label="가장 보람된 성취" value={selectedUser.proudAchievement} fullWidth field="proudAchievement" />
                    <ProfileField label="중요한 가치 3가지" value={selectedUser.importantValues} fullWidth field="importantValues" />
                    <ProfileField label="10년 뒤 나의 모습" value={selectedUser.futureVision} fullWidth field="futureVision" />
                  </ProfileSection>

                  {/* 이상형/결혼 */}
                  <ProfileSection title="이상형/결혼관">
                    <ProfileField label="이상형" value={selectedUser.idealType} fullWidth field="idealType" />
                    <ProfileField label="결혼 목표" value={selectedUser.marriageGoal} field="marriageGoal" />
                  </ProfileSection>

                  {/* 자기소개 */}
                  {(editMode || selectedUser.introduction) && (
                    <ProfileSection title="자기소개">
                      <ProfileField label="자기소개" value={selectedUser.introduction} fullWidth field="introduction" />
                    </ProfileSection>
                  )}
                </div>
              </div>

              {/* Account Info */}
              <div className="border-t border-stone-200 pt-4" data-pdf-hide>
                <p className="text-sm font-medium text-stone-700 mb-3">계정 정보</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-stone-500">가입일:</span>
                    <span className="ml-2 text-stone-700">
                      {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString("ko-KR") : "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-stone-500">포인트:</span>
                    <span className="ml-2 text-stone-700">{selectedUser.points || 0} P</span>
                  </div>
                  {selectedUser.upgrade_date && (
                    <div>
                      <span className="text-stone-500">업그레이드일:</span>
                      <span className="ml-2 text-stone-700">
                        {new Date(selectedUser.upgrade_date).toLocaleDateString("ko-KR")}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Membership Change */}
              <div className="border-t border-stone-200 pt-4" data-pdf-hide>
                <p className="text-sm font-medium text-stone-700 mb-3">멤버십 변경</p>
                <div className="flex gap-2">
                  {["basic", "regular", "premium", "vip"].map((type) => {
                    const isCurrent = normalizeMembership(selectedUser.membership_type) === type;
                    return (
                      <button
                        key={type}
                        onClick={() => handleChangeMembership(selectedUser.id, type)}
                        disabled={isCurrent}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isCurrent
                            ? "bg-burgundy-500 text-white"
                            : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                        }`}
                        data-testid={`membership-btn-${type}`}
                      >
                        {membershipLabels[type]?.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            </AdminEditContext.Provider>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-[60] p-4 cursor-zoom-out"
          onClick={() => setPreviewImage(null)}
          data-testid="admin-user-image-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="닫기"
            data-testid="admin-user-image-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={previewImage && !previewImage.startsWith("http") ? `${API_URL}${previewImage}` : previewImage}
            alt="프로필 사진 확대"
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* User Consent Modal */}
      {consentModalUserId && (
        <UserConsentModal
          userId={consentModalUserId}
          token={token}
          onClose={() => setConsentModalUserId(null)}
        />
      )}

      {/* 서류(혼인관계증명서) 미리보기 모달 */}
      {docPreviewUrl && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={closeDocPreview} data-testid="doc-preview-modal">
          <div className="max-w-3xl max-h-[85vh] bg-white rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-stone-200">
              <span className="text-sm font-medium text-stone-600">서류 미리보기 (혼인관계증명서)</span>
              <button onClick={closeDocPreview} className="p-1 hover:bg-stone-100 rounded" data-testid="doc-preview-close"><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            <div className="p-4 overflow-auto max-h-[75vh]">
              {docPreviewType === "pdf" ? (
                <iframe src={docPreviewUrl} className="w-full h-[70vh]" title="서류 미리보기" />
              ) : (
                <img src={docPreviewUrl} alt="혼인관계증명서" className="max-w-full rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* 관리자 → 회원 1:1 쪽지 모달 */}
      <DMModal
        open={!!dmTarget}
        onClose={() => setDmTarget(null)}
        userId={dmTarget?.id}
        userName={dmTarget?.name}
      />
    </div>
  );
}

// Info Item Component
const InfoItem = ({ icon, label, value, highlight, pdfHide, field, dualFields, type }) => {
  const { editing, editData, onEdit } = useContext(AdminEditContext);
  const editable = editing && (field || dualFields);
  return (
    <div {...(pdfHide ? { "data-pdf-hide": "" } : {})} className={`flex items-start gap-3 p-3 rounded-xl ${highlight ? "bg-amber-50 border border-amber-200" : "bg-stone-50"}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${highlight ? "bg-amber-100 text-amber-600" : "bg-stone-200 text-stone-500"}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${highlight ? "text-amber-600" : "text-stone-500"}`}>{label}</p>
        {editable ? (
          dualFields ? (
            <div className="flex gap-2 mt-1">
              {dualFields.map((df) => (
                <input
                  key={df.key}
                  type="text"
                  value={editData[df.key] ?? ""}
                  onChange={(e) => onEdit(df.key, e.target.value)}
                  placeholder={df.ph}
                  className="w-full px-2 py-1 rounded-md border border-stone-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none text-sm"
                  data-testid={`admin-edit-${df.key}`}
                />
              ))}
            </div>
          ) : (
            <input
              type={type || "text"}
              value={editData[field] ?? ""}
              onChange={(e) => onEdit(field, e.target.value)}
              className="w-full mt-1 px-2 py-1 rounded-md border border-stone-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none text-sm"
              data-testid={`admin-edit-${field}`}
            />
          )
        ) : (
          <p className="text-sm font-medium text-stone-800 break-words">{value}</p>
        )}
      </div>
    </div>
  );
};

// Profile Section Component
const ProfileSection = ({ title, children }) => (
  <div className="border-b border-stone-200 pb-4 last:border-0 last:pb-0">
    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">{title}</p>
    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {children}
    </div>
  </div>
);

// Profile Field Component
const ProfileField = ({ label, value, fullWidth, field, type }) => {
  const { editing, editData, onEdit } = useContext(AdminEditContext);
  const editable = editing && field;
  if (!editable && !value) return null;
  return (
    <div className={fullWidth ? "col-span-2" : ""}>
      <span className="text-xs text-stone-400">{label}:</span>
      {editable ? (
        fullWidth ? (
          <textarea
            value={editData[field] ?? ""}
            onChange={(e) => onEdit(field, e.target.value)}
            rows={2}
            className="w-full mt-1 px-2 py-1 rounded-md border border-stone-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none text-sm resize-y"
            data-testid={`admin-edit-${field}`}
          />
        ) : (
          <input
            type={type || "text"}
            value={editData[field] ?? ""}
            onChange={(e) => onEdit(field, e.target.value)}
            className="w-full mt-1 px-2 py-1 rounded-md border border-stone-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none text-sm"
            data-testid={`admin-edit-${field}`}
          />
        )
      ) : (
        <p className="text-sm text-stone-700 break-words">{value}</p>
      )}
    </div>
  );
};
